/* eslint-disable react/no-array-index-key */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Lightbox from 'react-18-image-lightbox';
import { filesize } from 'filesize';

import ImageComponent from './ImageComponent';
import AttachmentsModal from './AttachmentsModal';

import download from '../../../utils/download';
import { cn } from '@/lib/utils';
import { IconButton } from '@/components/ui/custom/IconButton';
import { ArrowDownToLine } from 'lucide-react';
import { CustomButton } from '@/components/ui/custom/CustomButton';
import { useEffect } from 'react';

const MAX_IMAGE_PREVIEW_HEIGHT = 80;

const AttachmentSidebarViewer = ({
	isAttachmentsModalOpen,
	handleIsAttachmentsModalOpen,
}) => {
	// const [isModalOpen, setIsModalOpen] = useState(false);
	const [isLightboxOpen, setIsLightboxOpen] = useState(false);
	const [photoIndex, setPhotoIndex] = useState(0);
	const teams = useSelector((state) => state.teams);

	const { _activeTeamConversation } = teams;

	const activeTeamConversation = teams.teamConversations.find(
		(conv) => conv._id === _activeTeamConversation
	);

	const attachments = activeTeamConversation?.attachments;

	const [localAttachments, setLocalAttachments] = useState(attachments || []);

	// Before any early return, so the hooks run in the same order every render.
	useEffect(() => {
		setLocalAttachments(attachments || []);
	}, [attachments]);

	const openLightbox = (index) => {
		setPhotoIndex(index);
		setIsLightboxOpen(true);
	};

	if (!teams || !teams.teamConversations || !teams.teamConversations.length) {
		return null;
	}

	const sortedLocalAttachments = localAttachments
		.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
		.slice(0, 4)
		.reverse();

	const saveImage = (attachment, imageBlob) => {
		setLocalAttachments((prevAttachments) => {
			const attachmentIndex = prevAttachments.findIndex(
				(a) => a.url === attachment.url
			);

			if (attachmentIndex === -1) {
				return prevAttachments;
			}

			const newAttachments = [...prevAttachments];

			newAttachments[attachmentIndex] = {
				...newAttachments[attachmentIndex],
				imageBlob,
			};

			return newAttachments;
		});
	};

	if (!attachments || attachments.length === 0) {
		return (
			<div className="flex flex-col text-muted-foreground text-xs text-center">
				<p>No Media</p>
				<p>Shared photos and videos you exchange will appear here.</p>
			</div>
		);
	}

	return (
		<div className="w-full">
			{isLightboxOpen && (
				<Lightbox
					mainSrc={sortedLocalAttachments[photoIndex].imageBlob}
					nextSrc={
						sortedLocalAttachments[
							(photoIndex + 1) % sortedLocalAttachments.length
						]
					}
					prevSrc={
						sortedLocalAttachments[
							(photoIndex + sortedLocalAttachments.length - 1) %
								sortedLocalAttachments.length
						]
					}
					onCloseRequest={() => setIsLightboxOpen(false)}
					reactModalStyle={{ zIndex: 99999 }}
					onMovePrevRequest={() =>
						setPhotoIndex(
							(photoIndex + sortedLocalAttachments.length - 1) %
								sortedLocalAttachments.length
						)
					}
					onMoveNextRequest={() =>
						setPhotoIndex(
							(photoIndex + sortedLocalAttachments.length + 1) %
								sortedLocalAttachments.length
						)
					}
				/>
			)}

			{/* {!isAttachmentsModalOpen && ( */}
			<AttachmentsModal
				isAttachmentsModalOpen={isAttachmentsModalOpen}
				handleIsAttachmentsModalOpen={handleIsAttachmentsModalOpen}
				// attachments={attachments}
				_teamConversation={_activeTeamConversation}
			/>
			{/* // )} */}

			{sortedLocalAttachments.map((attachment, index) => {
				return (
					<div
						key={attachment.url}
						className={cn(
							'p-1 w-full space-between flex items-center hover:bg-stone-100 dark:hover:bg-stone-800 rounded-md cursor-pointer',
							'max-md:gap-1'
						)}
					>
						<div
							className="flex justify-center rounded-md sm:max-w-5 md:min-w-20 max-md:size-14 max-md:max-w-none max-md:shrink-0 max-md:overflow-hidden"
							style={{
								maxHeight: MAX_IMAGE_PREVIEW_HEIGHT,
								margin: 4,
							}}
						>
							<ImageComponent
								src={attachment.url}
								name={attachment.name}
								item={attachment}
								onImageLoad={(imageBlob) =>
									saveImage(attachment, imageBlob)
								}
								openLightbox={() => openLightbox(index)}
							/>
						</div>
						<div className="flex flex-col space-y-1.5 md:min-w-10 lg:w-full mx-2 max-md:min-w-0 max-md:flex-1 max-md:space-y-0.5">
							<p className="text-ellipsis line-clamp-2 text-wrap break-words text-xs max-md:text-sm">
								{attachment.name}
							</p>
							<p className="text-ellipsis line-clamp-2 text-wrap break-words text-[10px]">
								{attachment.type}
							</p>
							<p className="text-ellipsis line-clamp-2 text-wrap break-words text-[10px]">
								{filesize(Number(attachment.size || 0), {
									base: 2,
									standard: 'jedec',
								})}
							</p>
						</div>

						<div className="flex items-center w-fit">
							<IconButton
								onClick={() => {
									download(
										attachment.imageBlob,
										attachment.name
									);
								}}
								aria-label={`Download ${attachment.name || 'attachment'}`}
								className="hover:bg-[#000000] hover:bg-opacity-10"
							>
								<ArrowDownToLine className="size-4 text-muted-foreground" />
							</IconButton>
						</div>
					</div>
				);
			})}
			{attachments?.length > 0 && (
				<CustomButton
					variant="outline"
					onClick={() => handleIsAttachmentsModalOpen(true)}
					size="sm"
					className="w-full mt-2 max-md:h-11 max-md:text-sm"
				>
					View all
				</CustomButton>
			)}
		</div>
	);
};

export default AttachmentSidebarViewer;
